import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { EyeIcon, EyeSlashIcon, LockClosedIcon  } from '@heroicons/react/24/outline';
import 'react-toastify/dist/ReactToastify.css';
import Breadcrumb from '../common/Breadcrumb';
import { useChangePassword } from '../../context/ChangePasswordContext';
import Swal from 'sweetalert2';
import { useLoading } from '../../context/LoadingContext';
import { useNavigate } from 'react-router-dom';

// Validation schema
const schema = yup.object().shape({
  current_password: yup.string().required('Current password is required'),
  new_password: yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~])[A-Za-z\d!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]{8,}$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character.'
    ),
  confirm_password: yup.string()
    .required('Please confirm your password')
    .oneOf([yup.ref('new_password')], 'The confirm password field must match new password.')
});

const ChangePassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { 
    changePassword, 
    formErrors, 
    successMessage 
  } = useChangePassword();
  const navigate = useNavigate();

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
      
  } = useForm({
    resolver: yupResolver(schema)
  });
   

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading();
    
    const response = await changePassword(data);
     
    if (response.status === 200) {
      Swal.fire({
        title: 'Success',
        text: 'Password updated successfully!',
        icon: 'success'
      }).then(() => {
        // Navigate after success
        navigate('/dashboard');
      });
      reset();
    } else {
      
      if (response.errors) {
        Object.entries(response.errors).forEach(([field, message]) => {
          setError(field, {
            type: 'manual',
            message,
          });
        });
      } else if (response.error?.message) {
        setError('general', {
          type: 'manual',
          message: response.error.message,
        });
      }
    }
    setIsLoading(false);
    hideLoading();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Change Password</h1>
      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Current Password</label>
            <div className="mt-1 relative rounded-md shadow-sm">
               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400"/>
                  
                </div>
              <input
                type={showCurrentPassword ? "text" : "password"}
                {...register('current_password')}
                className={`appearance-none block w-full pl-10 px-3 py-2 border ${
                  errors.current_password ? 'border-red-300' : 'border-gray-300'
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                 
                {showCurrentPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
                
              </button>
            </div>
            {errors.current_password && (
              <p className="mt-1 text-sm text-red-600">
                {errors.current_password.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">New Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400"/>
                  
                </div>
              <input
                type={showNewPassword ? "text" : "password"}
                {...register('new_password')}
                className={`appearance-none block w-full pl-10 px-3 py-2 border ${
                  errors.new_password ? 'border-red-300' : 'border-gray-300'
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showNewPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {errors.new_password && (
              <p className="mt-1 text-sm text-red-600">
                {errors.new_password.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
            <div className="relative">
               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400"/>
                  
                </div>
              <input
                type={showConfirmPassword ? "text" : "password"}
                {...register('confirm_password')}
                className={`appearance-none block w-full pl-10 px-3 py-2 border ${
                  errors.confirm_password ? 'border-red-300' : 'border-gray-300'
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {errors.confirm_password && (
              <p className="mt-1 text-sm text-red-600">
                {errors.confirm_password.message}
              </p>
            )}
          </div>

          {formErrors?.general && (
            <div className="text-red-600 text-sm mt-2">{formErrors.general}</div>
          )}
          {successMessage && <div className="success">{successMessage}</div>}

          <div className="flex justify-end">
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
              disabled={isLoading}
            >
              {isLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                'Update Password'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword; 